import _ from 'lodash'
import moment from 'moment'
import {MerchantTerminalType} from '@/stores/pay-by-link/models/MerchantTerminalType'
import {TerminalType} from '@/constants'
import {DEFAULT_CURRENCY} from '@/constants/currencies'
import {MerchantTerminalSelectType} from '@/stores/pay-by-link/models/MerchantTerminalSelectType'
import {CreatePaymentLinkViewType, PaymentLinkOptionMode, PaymentReferenceMode} from '@/api'
import {StoreWithTerminalsType} from '@/stores/store-and-terminals'

export const getStoreTerminal = (store: StoreWithTerminalsType) => {
    if (!store.isActive || !store.terminals || store.terminals.length === 0) return null

    const activeTerminals = store.terminals.filter((t) => t.isActive)
    if (activeTerminals.length === 0) return null
    if (activeTerminals.length === 1) return activeTerminals[0]

    const defaultTerminals = activeTerminals.filter((t) => t.isDefault)
    if (defaultTerminals.length === 0) return activeTerminals[0]
    return defaultTerminals[0]
}

export const getEcomTerminalsSelectType = (stores: StoreWithTerminalsType[]) => {
    return stores
        ?.map((s) => ({...s, terminal: getStoreTerminal(s)}))
        .filter((s) => s.terminal && s.type === TerminalType.ECOM)
        .map((s) => ({value: s.terminal.id, label: s.name, data: getTerminalData(s.terminal)}))
}

export const getTerminalData = (terminal: MerchantTerminalType) => {
    const {settings, defaultCurrency, supportedCurrencies} = terminal
    return {
        ...settings,
        defaultCurrency: defaultCurrency || DEFAULT_CURRENCY,
        supportedCurrencies: supportedCurrencies || [DEFAULT_CURRENCY]
    }
}

export const findTerminalData = (
    terminalId: string,
    terminals: MerchantTerminalSelectType[] = []
) => {
    const terminal = terminals.find((t) => t.value === terminalId)
    return (
        (terminal && terminal.data) || {
            defaultCurrency: DEFAULT_CURRENCY,
            supportedCurrencies: [DEFAULT_CURRENCY]
        }
    )
}

export const formatCreatePayLinkRequest = (
    values: CreatePaymentLinkViewType,
    terminalId: string
) => {
    const formattedValues = _.cloneDeep(values)

    formattedValues.currency = formattedValues.currency || DEFAULT_CURRENCY
    formattedValues.amount = Number(formattedValues.presetAmount ?? formattedValues.amount ?? 0)

    if (!formattedValues.terminalId) {
        formattedValues.terminalId = terminalId
    }

    if (!formattedValues.expirationDate) {
        formattedValues.expirationDate = moment().add(3, 'years').toDate()
    }

    const min = Number(formattedValues.minAmount)
    const max = Number(formattedValues.maxAmount)
    const hasRange = Number.isFinite(min) && Number.isFinite(max) && min < max
    const isMultiUse = formattedValues.type === 'multiUse'
    const canCustomerSetAmount = formattedValues.canCustomerSetAmount
    const paymentReferenceMode =
        formattedValues.paymentReferenceMode || PaymentReferenceMode.FIXED

    const linkOptions =
        isMultiUse && canCustomerSetAmount
              ? {
                  mode: PaymentLinkOptionMode.REUSABLE_VARIABLE,
                  paymentReferenceMode,
                  ...(hasRange ? {minAmount: min, maxAmount: max} : {})
              }
            : isMultiUse
              ? {mode: PaymentLinkOptionMode.REUSABLE_FIXED, paymentReferenceMode}
              : {mode: PaymentLinkOptionMode.ONE_OFF_FIXED, paymentReferenceMode}

    delete formattedValues.recurring
    delete formattedValues.minAmount
    delete formattedValues.maxAmount
    delete formattedValues.type
    delete formattedValues.canCustomerSetAmount
    delete formattedValues.presetAmount
    delete formattedValues.paymentReferenceMode
    delete formattedValues.expirationDatePreset

    if (values.recurring) {
        return {
            ...formattedValues,
            linkOptions,
            periodic: {
                periodicType: 'ucof'
            }
        }
    }

    return {
        ...formattedValues,
        linkOptions
    }
}
